import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection, ScheduleType } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Reminder from 'webapp/models-custom/reminder';
import type ReportSchedule from 'webapp/models-custom/report-schedule';
import type ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';

/* import { Status as ReportScheduleStatus } from 'webapp/models-custom/report-schedule'; */

export default class FilterSortReportSchedule extends FilterSort<ReportSchedule> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	@tracked override sortProp = 'scheduledOn';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override searchProps: SearchProps = {
		'report.title': 2,
		'report.ownerUser.name': 1,
	};

	override itemDomSelector = '.table-row';

	override settingsNamePrefix = 'reportSchedulesFilterSettings';

	constructor(options: FilterSortSettings<ReportSchedule>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Status
		/* this.createFilter<number>({
			id: 1,
			label: 'Status',
			name: 'status',
			selected: selectedFilters[1],
			getOptions: () => {
				return this.statusOptions;
			},
			filterFn: (item, selected) => {
				return item.sendStatus === selected;
			},
		}); */

		// Reminder type
		this.createFilter<number>({
			id: 5,
			label: 'Reminder',
			name: 'reminder',
			selected: selectedFilters[5] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.scheduleOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) => {
					const report = item.report;
					return (
						!!report &&
						report.reminders.some((r: Reminder) => r.scheduleType === selectedValue)
					);
				});
			},
		});

		// Schedule type
		this.createFilter<number>({
			id: 4,
			label: 'Schedule',
			name: 'schedule',
			selected: selectedFilters[4] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.scheduleOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) => {
					const report = item.report;
					return (
						!!report &&
						report.scheduleSettings.some(
							(ss: ReportScheduleSetting) => ss.scheduleType === selectedValue,
						)
					);
				});
			},
		});

		// Owner
		this.createFilter<number>({
			id: 2,
			label: 'Owned by',
			name: 'owner',
			selected: selectedFilters[2] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.ownerOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					const report = item.report;
					return !!report && report.ownerId === selectedValue;
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 3,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[3] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					const report = item.report;
					return !!report && report.tags.includes(selectedValue);
				});
			},
		});
	}

	/*
	get statusOptions(): FilterOption<number>[] {
		//ready to be sent, waiting for approval, skipped…
		return [
			{id: 0, text: 'All'},
			{id: ReportScheduleStatus.READY, text: 'Ready to be sent'},
			{id: ReportScheduleStatus.WAITING, text: 'Waiting for approval'},
			{id: ReportScheduleStatus.SKIPPED, text: 'Skipped'},
		];
	} */

	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.mmReportsService.savedRecords.forEach((item) => {
			const ownerId = item.ownerId!; // without an owner, counted under the "null" key
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}

	get tagOptions(): FilterOption<number>[] {
		const values = this.mmReportsService.assignedTags,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get scheduleOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [
			{ id: ScheduleType.DAILY, text: 'Daily' },
			{ id: ScheduleType.WEEKLY, text: 'Weekly' },
			{ id: ScheduleType.MONTHLY, text: 'Monthly' },
			{ id: ScheduleType.QUARTERLY, text: 'Quarterly' },
		];

		list.unshift({ id: 0, text: 'All' });

		return list;
	}
}
